// localStorage state machine for the workspace onboarding tour.
// Mirrors ../openclaw_setup/frontend/src/utils/firstInstanceOnboarding.ts,
// minus the instanceId argument — Prufa's dashboard is flat-workspace
// (no per-instance concept in the UI), so the sentinels are just '1'.
//
// Four orthogonal flags per user, all in localStorage:
//   pending   — tour is queued; auto-show on next mount
//   active    — tour is open right now (manual start)
//   completed — user finished the tour; never auto-show again
//   dismissed — user closed without finishing; never auto-show again
//               (manual "Take the tour" button still works — same UX
//               invariant as openclaw; see test #8)
//
// Cross-component sync is via a window CustomEvent
// (TOUR_UPDATED_EVENT). Storage is namespaced per userId so the tour
// follows the workspace identity (the workspace_id, not email, is the
// stable identifier — see CLAUDE.md identity model).

const STORAGE_PREFIX = 'prufa.onboarding-tour'
export const TOUR_UPDATED_EVENT = 'prufa:onboarding-tour-updated'

function pendingKey(userId) {
  return `${STORAGE_PREFIX}.pending.${userId}`
}
function activeKey(userId) {
  return `${STORAGE_PREFIX}.active.${userId}`
}
function completedKey(userId) {
  return `${STORAGE_PREFIX}.completed.${userId}`
}
function dismissedKey(userId) {
  return `${STORAGE_PREFIX}.dismissed.${userId}`
}

function notifyTourStateChanged() {
  try {
    window.dispatchEvent(new CustomEvent(TOUR_UPDATED_EVENT))
  } catch {
    // ignore event dispatch failures
  }
}

export function queueFirstTour(userId) {
  try {
    if (!userId) return
    if (hasDismissedFirstTour(userId) || hasCompletedFirstTour(userId)) return
    window.localStorage.setItem(pendingKey(userId), '1')
    notifyTourStateChanged()
  } catch {
    // ignore storage failures
  }
}

export function startFirstTour(userId) {
  try {
    if (!userId) return
    window.localStorage.setItem(activeKey(userId), '1')
    notifyTourStateChanged()
  } catch {
    // ignore storage failures
  }
}

export function getActiveFirstTour(userId) {
  try {
    if (!userId) return null
    return window.localStorage.getItem(activeKey(userId))
  } catch {
    return null
  }
}

export function getPendingFirstTour(userId) {
  try {
    if (!userId) return null
    return window.localStorage.getItem(pendingKey(userId))
  } catch {
    return null
  }
}

export function clearPendingFirstTour(userId) {
  try {
    if (!userId) return
    window.localStorage.removeItem(pendingKey(userId))
    notifyTourStateChanged()
  } catch {
    // ignore storage failures
  }
}

export function clearActiveFirstTour(userId) {
  try {
    if (!userId) return
    window.localStorage.removeItem(activeKey(userId))
    notifyTourStateChanged()
  } catch {
    // ignore storage failures
  }
}

export function markFirstTourCompleted(userId) {
  try {
    if (!userId) return
    window.localStorage.setItem(completedKey(userId), '1')
    window.localStorage.removeItem(pendingKey(userId))
    window.localStorage.removeItem(activeKey(userId))
    notifyTourStateChanged()
  } catch {
    // ignore storage failures
  }
}

export function postponeFirstTour(userId) {
  try {
    if (!userId) return
    window.localStorage.setItem(pendingKey(userId), '1')
    window.localStorage.removeItem(activeKey(userId))
    notifyTourStateChanged()
  } catch {
    // ignore storage failures
  }
}

export function dismissFirstTour(userId) {
  try {
    if (!userId) return
    window.localStorage.setItem(dismissedKey(userId), '1')
    window.localStorage.removeItem(pendingKey(userId))
    window.localStorage.removeItem(activeKey(userId))
    notifyTourStateChanged()
  } catch {
    // ignore storage failures
  }
}

export function hasCompletedFirstTour(userId) {
  try {
    if (!userId) return false
    return window.localStorage.getItem(completedKey(userId)) === '1'
  } catch {
    return false
  }
}

export function hasDismissedFirstTour(userId) {
  try {
    if (!userId) return false
    return window.localStorage.getItem(dismissedKey(userId)) === '1'
  } catch {
    return false
  }
}

// Clears all four sentinels and re-emits the update event. Used by the
// "Reset onboarding tour" button in Workspace settings.
export function resetFirstTour(userId) {
  try {
    if (!userId) return
    window.localStorage.removeItem(pendingKey(userId))
    window.localStorage.removeItem(activeKey(userId))
    window.localStorage.removeItem(completedKey(userId))
    window.localStorage.removeItem(dismissedKey(userId))
    notifyTourStateChanged()
  } catch {
    // ignore storage failures
  }
}
